import {
  JsonValueSchema,
  RuntimeProbeSnapshotV1Schema,
} from '@mintyads/shared-types';
import type {
  RuntimeProbeInputEvent,
  RuntimeProbeSnapshot,
  RuntimeProbeTransform,
  RuntimeProbeTweenSnapshot,
} from './runtimeProbeTypes';

export interface RuntimeProbeCollectorInput {
  readonly projectId: string;
  readonly runtimeVersion: string;
  readonly label: string;
  readonly frameIndex: number;
  readonly capturedAtMs?: number | undefined;
  readonly transform: RuntimeProbeTransform;
  readonly targetRegistry: ReadonlyMap<string, Record<string, unknown>>;
  readonly recentInput: readonly RuntimeProbeInputEvent[];
  readonly activeTweens: readonly RuntimeProbeTweenSnapshot[];
  readonly stateProviders: ReadonlyMap<string, () => unknown>;
}

interface RuntimeProbeRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

interface RuntimeProbeSemantic {
  readonly role: string;
  readonly source: 'explicit' | 'id' | 'type' | 'unknown';
  readonly flags: readonly string[];
}

function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function readNumber(target: Record<string, unknown>, key: string): number | undefined {
  const value = target[key];
  return finite(value) ? value : undefined;
}

function readScale(target: Record<string, unknown>, axis: 'x' | 'y'): number | undefined {
  const scale = target['scale'];
  if (finite(scale)) return scale;
  if (scale && typeof scale === 'object' && axis in scale) {
    const value = (scale as Record<string, unknown>)[axis];
    if (finite(value)) return value;
  }
  return undefined;
}

function isFiniteRect(rect: RuntimeProbeRect): boolean {
  return finite(rect.x) && finite(rect.y) && finite(rect.width) && finite(rect.height);
}

function readBounds(target: Record<string, unknown>): RuntimeProbeRect | 'missing' | 'non-finite' {
  const getBounds = target['getBounds'];
  if (typeof getBounds !== 'function') return 'missing';
  const raw = getBounds.call(target) as unknown;
  if (!raw || typeof raw !== 'object') return 'non-finite';
  const candidate = 'rectangle' in raw
    ? (raw as { readonly rectangle?: unknown }).rectangle
    : raw;
  if (!candidate || typeof candidate !== 'object') return 'non-finite';
  const record = candidate as Record<string, unknown>;
  const rect = {
    x: record.x,
    y: record.y,
    width: record.width,
    height: record.height,
  };
  if (!finite(rect.x) || !finite(rect.y) || !finite(rect.width) || !finite(rect.height)) return 'non-finite';
  return {
    x: rect.x,
    y: rect.y,
    width: rect.width,
    height: rect.height,
  };
}

function readRectLike(value: unknown): RuntimeProbeRect | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const rect = {
    x: record.x,
    y: record.y,
    width: record.width,
    height: record.height,
  };
  if (!finite(rect.x) || !finite(rect.y) || !finite(rect.width) || !finite(rect.height)) return null;
  return {
    x: rect.x,
    y: rect.y,
    width: rect.width,
    height: rect.height,
  };
}

function clientRectFrom(bounds: RuntimeProbeRect, transform: RuntimeProbeTransform): RuntimeProbeRect {
  return {
    x: transform.canvasClientRect.x + bounds.x,
    y: transform.canvasClientRect.y + bounds.y,
    width: bounds.width,
    height: bounds.height,
  };
}

function readTextureSnapshot(target: Record<string, unknown>, bounds: RuntimeProbeRect | null) {
  const texture = target['texture'];
  if (!texture || typeof texture !== 'object') return undefined;
  const record = texture as Record<string, unknown>;
  const source = record.source && typeof record.source === 'object'
    ? record.source as Record<string, unknown>
    : {};
  const naturalWidth = finite(source.width) ? source.width : (finite(record.width) ? record.width : undefined);
  const naturalHeight = finite(source.height) ? source.height : (finite(record.height) ? record.height : undefined);
  const assetUrl = typeof record.assetUrl === 'string' ? record.assetUrl : undefined;
  const displayWidth = bounds?.width;
  const displayHeight = bounds?.height;
  const fillRatio = finite(naturalWidth) && finite(naturalHeight) && bounds !== null
    ? Math.max(bounds.width / naturalWidth, bounds.height / naturalHeight)
    : undefined;
  return {
    ...(assetUrl === undefined ? {} : { assetUrl }),
    ...(naturalWidth === undefined ? {} : { naturalWidth }),
    ...(naturalHeight === undefined ? {} : { naturalHeight }),
    ...(displayWidth === undefined ? {} : { displayWidth }),
    ...(displayHeight === undefined ? {} : { displayHeight }),
    ...(fillRatio === undefined ? {} : { fillRatio }),
  };
}

function readContentSnapshot(target: Record<string, unknown>, texture: ReturnType<typeof readTextureSnapshot>) {
  const text = target.text;
  const assetUrl = typeof target.assetUrl === 'string'
    ? target.assetUrl
    : texture && typeof texture.assetUrl === 'string'
      ? texture.assetUrl
      : undefined;
  const output = {
    ...(typeof text === 'string' ? { text } : {}),
    ...(assetUrl === undefined ? {} : { assetUrl }),
  };
  return Object.keys(output).length === 0 ? undefined : output;
}

function inferTargetType(target: Record<string, unknown>): string {
  if (typeof target['mockType'] === 'string') return target['mockType'];
  if (target['texture'] !== undefined) return 'sprite';
  const ctor = (target as { readonly constructor?: { readonly name?: string } }).constructor?.name;
  return ctor && ctor !== 'Object' ? ctor : 'display-object';
}

function readParent(target: Record<string, unknown>): Record<string, unknown> | null {
  const parent = target['parent'];
  return parent !== null && typeof parent === 'object' && !Array.isArray(parent) ? parent as Record<string, unknown> : null;
}

function targetIdMap(targetRegistry: ReadonlyMap<string, Record<string, unknown>>): Map<Record<string, unknown>, string> {
  const output = new Map<Record<string, unknown>, string>();
  for (const [id, target] of targetRegistry.entries()) output.set(target, id);
  return output;
}

function parentIdFor(target: Record<string, unknown>, idsByTarget: ReadonlyMap<Record<string, unknown>, string>): string | null {
  const parent = readParent(target);
  return parent ? idsByTarget.get(parent) ?? null : null;
}

function depthFor(target: Record<string, unknown>): number {
  let depth = 0;
  let current = readParent(target);
  while (current && depth < 64) {
    depth += 1;
    current = readParent(current);
  }
  return depth;
}

function effectiveDisplayState(target: Record<string, unknown>): { readonly visible: boolean; readonly renderable: boolean | undefined } {
  let current: Record<string, unknown> | null = target;
  let visible = true;
  let renderable: boolean | undefined = typeof target.renderable === 'boolean' ? target.renderable : undefined;
  let depth = 0;
  while (current && depth < 64) {
    if (current.visible === false) visible = false;
    if (current.renderable === false) renderable = false;
    current = readParent(current);
    depth += 1;
  }
  if (!visible) renderable = false;
  return { visible, renderable };
}

function readInteractivity(target: Record<string, unknown>, visible: boolean, renderable: boolean | undefined) {
  const eventMode = typeof target.eventMode === 'string' ? target.eventMode : undefined;
  const interactive = typeof target.interactive === 'boolean' ? target.interactive : undefined;
  const hitArea = readRectLike(target.hitArea);
  const acceptsPointerEvents = visible
    && renderable !== false
    && (interactive === true || eventMode === 'static' || eventMode === 'dynamic');
  return {
    ...(eventMode === undefined ? {} : { eventMode }),
    ...(interactive === undefined ? {} : { interactive }),
    hitArea,
    acceptsPointerEvents,
  };
}

function uniqueFlags(flags: readonly string[]): string[] {
  return [...new Set(flags.filter((flag) => flag.trim().length > 0))];
}

function readExplicitSemantic(target: Record<string, unknown>): RuntimeProbeSemantic | null {
  const raw = target.__mintyProbe;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  if (typeof record.role !== 'string' || record.role.trim().length === 0) return null;
  const flags = Array.isArray(record.flags)
    ? record.flags.filter((flag): flag is string => typeof flag === 'string')
    : [];
  return {
    role: record.role.trim(),
    source: 'explicit',
    flags: uniqueFlags(flags),
  };
}

function inferSemanticFromId(id: string, type: string, interactivity: { readonly acceptsPointerEvents: boolean }): RuntimeProbeSemantic {
  const lower = id.toLowerCase();
  const roleFlags = (role: string, flags: readonly string[], source: RuntimeProbeSemantic['source'] = 'id'): RuntimeProbeSemantic => ({
    role,
    source,
    flags: uniqueFlags(flags),
  });

  if (/background|backdrop|sky|scene-bg|playable-background/.test(lower)) {
    return roleFlags('background', ['decoration', 'non-draggable']);
  }
  if (/tutorial|hand|finger|gesture/.test(lower)) {
    return roleFlags('tutorial', ['overlay', 'instruction', 'non-draggable']);
  }
  if (/packshot|endcard/.test(lower)) {
    return roleFlags('endcard', ['overlay', 'post-gameplay']);
  }
  if (/cta|download|install|button/.test(lower)) {
    return roleFlags('cta', ['overlay', 'click-target']);
  }
  if (/objective|score|counter|hint|hud/.test(lower)) {
    return roleFlags('hud', ['ui', 'non-draggable']);
  }
  if (/logo|brand/.test(lower)) {
    return roleFlags('brand', ['ui', 'decoration', 'non-draggable']);
  }
  if (/match-board|board|playfield|grid/.test(lower)) {
    return roleFlags('board', ['match3', 'playfield', 'gameplay']);
  }
  if (/match-tile-\d+|tile-\d+|gem-\d+|piece-\d+/.test(lower)) {
    return roleFlags('tile', ['match3', 'gameplay', 'input-target']);
  }
  if (/^flower-\d+$/.test(lower)) {
    return roleFlags('objective', ['match3', 'gameplay', 'objective', 'feedback', 'non-draggable']);
  }
  if (/flower-host|grass|blocker|obstacle|static-cell|static|flower-\d+/.test(lower)) {
    return roleFlags('static-cell', ['match3', 'gameplay', 'static', 'non-draggable']);
  }
  if (/hero|player|runner|character|avatar/.test(lower)) {
    return roleFlags('player', ['gameplay', 'foreground', ...(interactivity.acceptsPointerEvents ? ['input-target'] : [])]);
  }
  if (/coin|collectible|reward|pickup|target/.test(lower)) {
    return roleFlags('collectible', ['gameplay', 'foreground']);
  }
  if (type === 'sprite') {
    return roleFlags('sprite', interactivity.acceptsPointerEvents ? ['input-candidate'] : [], 'type');
  }
  return roleFlags('unknown', [], 'unknown');
}

function collectStateProviders(
  providers: ReadonlyMap<string, () => unknown>,
  warnings: string[],
): Record<string, unknown> {
  const output: Record<string, unknown> = {};
  for (const [name, provider] of providers.entries()) {
    try {
      const value = provider();
      const parsed = JsonValueSchema.safeParse(value);
      if (!parsed.success) {
        warnings.push(`state provider ${name} returned non-json output`);
        continue;
      }
      output[name] = parsed.data;
    } catch (error) {
      warnings.push(`state provider ${name} failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
  return output;
}

export function collectRuntimeProbeSnapshot(input: RuntimeProbeCollectorInput): RuntimeProbeSnapshot {
  const warnings: string[] = [];
  const idsByTarget = targetIdMap(input.targetRegistry);
  const layers = [...input.targetRegistry.entries()].map(([id, target]) => {
    const displayState = effectiveDisplayState(target);
    const boundsResult = readBounds(target);
    let world: RuntimeProbeRect | null = null;
    if (boundsResult === 'missing') {
      warnings.push(`layer ${id} has no getBounds()`);
    } else if (boundsResult === 'non-finite' || !isFiniteRect(boundsResult)) {
      warnings.push(`layer ${id} returned non-finite bounds`);
    } else {
      world = boundsResult;
    }
    const texture = readTextureSnapshot(target, world);
    const content = readContentSnapshot(target, texture);
    const type = inferTargetType(target);
    const interactivity = readInteractivity(target, displayState.visible, displayState.renderable);
    const semantic = readExplicitSemantic(target) ?? inferSemanticFromId(id, type, interactivity);
    return {
      id,
      type,
      parentId: parentIdFor(target, idsByTarget),
      depth: depthFor(target),
      visible: displayState.visible,
      ...(displayState.renderable === undefined ? {} : { renderable: displayState.renderable }),
      ...(readNumber(target, 'alpha') === undefined ? {} : { alpha: readNumber(target, 'alpha') }),
      local: world,
      world,
      client: world === null ? null : clientRectFrom(world, input.transform),
      ...(readNumber(target, 'x') === undefined ? {} : { x: readNumber(target, 'x') }),
      ...(readNumber(target, 'y') === undefined ? {} : { y: readNumber(target, 'y') }),
      ...(readScale(target, 'x') === undefined ? {} : { scaleX: readScale(target, 'x') }),
      ...(readScale(target, 'y') === undefined ? {} : { scaleY: readScale(target, 'y') }),
      ...(readNumber(target, 'rotation') === undefined ? {} : { rotation: readNumber(target, 'rotation') }),
      ...(readNumber(target, 'zIndex') === undefined ? {} : { zIndex: readNumber(target, 'zIndex') }),
      ...(texture === undefined || Object.keys(texture).length === 0 ? {} : { texture }),
      ...(content === undefined ? {} : { content }),
      interactivity,
      semantic,
      tags: semantic.flags,
    };
  });

  const snapshot = {
    schemaVersion: 'runtime_probe.v1',
    projectId: input.projectId,
    runtimeVersion: input.runtimeVersion,
    capturedAtMs: input.capturedAtMs ?? Date.now(),
    frameIndex: input.frameIndex,
    label: input.label,
    transform: input.transform,
    layers,
    activeTweens: [...input.activeTweens],
    recentInput: [...input.recentInput],
    stateProviders: collectStateProviders(input.stateProviders, warnings),
    warnings,
  };

  return RuntimeProbeSnapshotV1Schema.parse(snapshot);
}
